import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FocusManager, trapFocus } from '../../utils/accessibility';

export interface AccessibleDialogProps extends DialogProps {
  /**
   * Dialog title (required for accessibility)
   */
  title: string;

  /**
   * Dialog content
   */
  children: React.ReactNode;

  /**
   * Action buttons (rendered in DialogActions)
   */
  actions?: React.ReactNode;

  /**
   * Callback when dialog closes
   */
  onClose: () => void;

  /**
   * Show close button in title bar
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Auto-focus first input on open
   * @default true
   */
  autoFocusFirstInput?: boolean;

  /**
   * Custom aria-describedby ID (if content has a description)
   */
  'aria-describedby'?: string;

  /**
   * Maximum width of dialog
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

/**
 * AccessibleDialog - Fully accessible dialog component
 *
 * Features:
 * - Focus trapping (Tab cycles through dialog elements)
 * - Focus restoration (returns focus when closed)
 * - ESC key closes dialog
 * - Auto-focus first input
 * - Proper ARIA labels
 * - Screen reader announcements
 *
 * Usage:
 * <AccessibleDialog
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   actions={
 *     <>
 *       <Button onClick={handleClose}>Cancel</Button>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <Typography>Dialog content here</Typography>
 * </AccessibleDialog>
 */
export const AccessibleDialog: React.FC<AccessibleDialogProps> = ({
  title,
  children,
  actions,
  onClose,
  showCloseButton = true,
  autoFocusFirstInput = true,
  maxWidth = 'sm',
  'aria-describedby': ariaDescribedBy,
  ...dialogProps
}) => {
  const theme = useTheme();
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusManager = useRef(new FocusManager());
  const titleId = useRef(`dialog-title-${Math.random().toString(36).substr(2, 9)}`);

  // Handle focus management
  useEffect(() => {
    if (dialogProps.open) {
      // Save current focus
      focusManager.current.capture();

      // Auto-focus first input if enabled
      if (autoFocusFirstInput) {
        setTimeout(() => {
          const firstInput = dialogRef.current?.querySelector<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
          );
          if (firstInput) {
            firstInput.focus();
          }
        }, 100);
      }
    } else {
      // Restore focus when dialog closes
      focusManager.current.restore();
    }
  }, [dialogProps.open, autoFocusFirstInput]);

  // Set up focus trap
  useEffect(() => {
    if (dialogProps.open && dialogRef.current) {
      const cleanup = trapFocus(dialogRef.current);
      return cleanup;
    }
  }, [dialogProps.open]);

  // Handle ESC key
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      {...dialogProps}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId.current}
      aria-describedby={ariaDescribedBy}
      onKeyDown={handleKeyDown}
      PaperProps={{
        ref: dialogRef,
        sx: {
          borderRadius: theme.shape.borderRadius * 1.5,
          ...dialogProps.PaperProps?.sx,
        },
        ...dialogProps.PaperProps,
      }}
    >
      <DialogTitle
        id={titleId.current}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: theme.typography.h5.fontFamily,
          fontWeight: 600,
          pb: 1,
        }}
      >
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            sx={{
              ml: 2,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          pb: 2,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AccessibleDialog;
